import { Component } from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { AccountService } from "../services";
import { ActivatedRoute, Router } from "@angular/router";
import { of, Subject } from "rxjs";
import {
  catchError,
  delay,
  finalize,
  map,
  takeUntil,
  tap,
} from "rxjs/operators";
import { ToastrService } from "ngx-toastr";
@Component({
  templateUrl: "reset-password.component.html",
  styleUrls: ["reset-password.component.scss"],
})
export class ResetPasswordComponent {
  forgotForm: FormGroup;
  resetPassForm: FormGroup;
  loading = false;
  submittedForgot = false;
  submittedPassword: boolean = false;
  returnUrl: string;
  error: string;
  code: string;
  token: string;
  isTokenValid: boolean = false;
  tokenVerified: any;
  disabled: boolean = false;
  showNewPassword: boolean = true;
  showConfirmPassword: boolean = true;
  errorRes: { status: boolean; mess: string } = null;
  onDestroy$: Subject<void> = new Subject<void>();
  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly accountService: AccountService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly toastr: ToastrService
  ) {}

  get fF() {
    return this.forgotForm.controls;
  }

  get fR() {
    return this.resetPassForm.controls;
  }

  ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      this.token = params["id"] ?? null;
      if (!this.token) {
        this.errorRes = {
          status: true,
          mess: "Invalid or missing token. Please request a new password reset link.",
        };
        this.isTokenValid = false;
      } else {
        this._verifyToken();
      }
    });

    this.forgotForm = this.formBuilder.group({
      email: ["", [Validators.required, Validators.email]],
    });

    this.resetPassForm = this.formBuilder.group({
      password: [
        "",
        [
          Validators.required,
          Validators.pattern(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@!$%&*])[A-Za-z\d@!$%&*]{10,}$/
          ),
        ],
      ],
      confirmPassword: ["", Validators.required],
    });
  }

  onForgotSubmit() {
    this.submittedForgot = true;
    if (this.forgotForm.invalid) {
      return;
    }
    this.error = null;
    this.loading = true;
    const email = this.fF.email.value;
    this.accountService
      .forgotPassword(email)
      .pipe(
        catchError((err) => {
          this.error = err.error?.message ?? "Unknown Error!";
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe(() => {
        if (!this.error) {
          this.toastr.success(
            "A password reset link has been sent to your registered email address",
            "Success"
          );
          this.router.navigateByUrl("/login");
        }
      });
  }

  onChangePasswordFormSubmit() {
    this.submittedPassword = true;
    if (!this.resetPassForm.invalid) {
      const { password, confirmPassword } = this.resetPassForm.controls;
      if (password.value !== confirmPassword.value) {
        this.errorRes = {
          status: true,
          mess: "New Password and Confirm Password don't match. Please Try again!",
        };
        return;
      }
      this.disabled = true;
      this.errorRes = null;
      this._handleChangePassword(password.value, confirmPassword.value);
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  _verifyToken() {
    this.accountService
      .verifyToken(this.token)
      .pipe(
        takeUntil(this.onDestroy$),
        map((resp) => {
          if (resp?.result?.tokenValid) {
            this.tokenVerified = resp.result;
            this.isTokenValid = true;
          } else {
            this.errorRes = {
              status: true,
              mess: "This link has expired. Please use the latest link received.",
            };
            this.isTokenValid = false;
          }
        }),
        catchError(() => {
          this.errorRes = {
            status: true,
            mess: "Error verifying token. Please try again.",
          };
          this.isTokenValid = false;
          return of(null);
        })
      )
      .subscribe();
  }

  _handleChangePassword(newPassword, confirmNewPassword) {
    this.accountService
      .resetPasswordWithToken({
        newPassword: newPassword,
        confirmPassword: confirmNewPassword,
        userName: this.tokenVerified.userName,
        token: this.token,
      })
      .pipe(
        takeUntil(this.onDestroy$),
        tap(() => {
          this.errorRes = {
            status: false,
            mess: "Your password has been changed successfully",
          };
        }),
        delay(3000),
        tap(() => {
          this.router.navigate(["/login"]);
        }),
        catchError((err) => {
          this.errorRes = { status: true, mess: err.error.message };
          return of(null);
        }),
        finalize(() => {
          this.disabled = false;
        })
      )
      .subscribe();
  }
}
